import type { ProxyHandlerConfig } from './types'

/**
 * vue-reactive-utils 默认配置
 */
export const DEFAULT_CONFIG: Required<ProxyHandlerConfig> = {
  deep: true,
  convertArray: true,
  onProxyDetected: () => {},
  onConversionError: () => {},
  warnInConsole: true,
  autoConvert: false,
  skipKeys: [],
}

/**
 * vue-reactive-utils 性能优化配置
 */
export const PERFORMANCE_CONFIG = {
  /** 最大递归深度，防止栈溢出 */
  MAX_DEPTH: 50,
  /** 最大对象大小，防止处理过大的对象 */
  MAX_OBJECT_SIZE: 10000,
  /** 最大数组长度，防止处理过大的数组 */
  MAX_ARRAY_LENGTH: 1000,
  /** 超时时间（毫秒），防止长时间处理 */
  TIMEOUT: 5000,
  /** 是否启用性能保护 */
  ENABLE_PERFORMANCE_GUARD: true,
}

/**
 * 合并配置，提供默认值
 */
export function mergeConfig<T extends ProxyHandlerConfig>(config: T = {} as T): Required<T> {
  return { ...DEFAULT_CONFIG, ...config } as Required<T>
}

/**
 * 检测是否为开发环境
 */
export function isDevelopment(): boolean {
  return (typeof window !== 'undefined' && window.location?.hostname === 'localhost')
    || (typeof globalThis !== 'undefined' && (globalThis as any).__DEV__)
}

/**
 * 构建路径字符串
 */
export function buildPath(basePath: string, key: string | number): string {
  return basePath === 'root' ? String(key) : `${basePath}.${key}`
}

/**
 * 获取对象属性类型
 */
export function getPropertyType(value: any, path: string): 'reactive' | 'ref' | 'readonly' {
  if (typeof value === 'object' && value !== null) {
    const key = path.split('.').pop() || ''
    const descriptor = Object.getOwnPropertyDescriptor(value, key)
    if (descriptor && !descriptor.writable) {
      return 'readonly'
    }
  }

  return 'reactive'
}

/**
 * 日志输出配置接口
 */
export interface LogMessageOptions {
  /** 日志级别 */
  level: 'warn' | 'error' | 'group' | 'groupEnd'
  /** 前缀标识 */
  prefix: string
  /** 主要消息 */
  message: string
  /** 附加数据 */
  data?: any
  /** 上下文信息 */
  context?: string
  /** 附加信息 */
  extra?: any
}

/**
 * 统一的日志输出函数
 *
 * @param options 日志输出配置
 *
 * @example
 * ```typescript
 * // 基本用法
 * logMessage({
 *   level: 'warn',
 *   prefix: 'ProxyHandler',
 *   message: '检测到 Proxy 对象',
 *   data: { path: 'user.profile', value: proxyObj }
 * })
 *
 * // 带上下文的用法
 * logMessage({
 *   level: 'error',
 *   prefix: 'PiniaProxy',
 *   message: 'Store 更新失败',
 *   data: error,
 *   context: 'userStore'
 * })
 * ```
 */
export function logMessage(options: LogMessageOptions): void {
  const { level, prefix, message, data, context, extra } = options
  const contextStr = context ? `[${context}]` : ''
  const fullMessage = `[${prefix}]${contextStr} ${message}`

  switch (level) {
    case 'warn':
      console.warn(fullMessage, data || '', { extra: extra || '' })
      break
    case 'error':
      console.error(fullMessage, data || '', { extra: extra || '' })
      break
    case 'group':
      console.group(fullMessage)
      break
    case 'groupEnd':
      console.groupEnd()
      break
  }
}

/**
 * 性能保护函数
 *
 * 优化说明：
 * 1. 优先检查数组长度（O(1)），避免不必要的对象检查
 * 2. 对于普通对象，使用更高效的属性计数方法
 * 3. 将最快速的检查放在前面，减少不必要的计算
 */
export function checkPerformanceGuard(
  obj: any,
  path: string,
  depth: number,
  startTime: number,
): { shouldContinue: boolean; reason?: string } {
  if (!PERFORMANCE_CONFIG.ENABLE_PERFORMANCE_GUARD) {
    return { shouldContinue: true }
  }

  // 检查递归深度（最快，优先检查）
  if (depth > PERFORMANCE_CONFIG.MAX_DEPTH) {
    return {
      shouldContinue: false,
      reason: `递归深度超过限制: ${depth} > ${PERFORMANCE_CONFIG.MAX_DEPTH}`,
    }
  }

  // 检查处理时间（快速，优先检查）
  const elapsed = Date.now() - startTime
  if (elapsed > PERFORMANCE_CONFIG.TIMEOUT) {
    return {
      shouldContinue: false,
      reason: `处理时间超过限制: ${elapsed}ms > ${PERFORMANCE_CONFIG.TIMEOUT}ms`,
    }
  }

  // 检查数组长度（O(1)，优先于对象检查）
  if (Array.isArray(obj)) {
    if (obj.length > PERFORMANCE_CONFIG.MAX_ARRAY_LENGTH) {
      return {
        shouldContinue: false,
        reason: `数组长度超过限制: ${obj.length} > ${PERFORMANCE_CONFIG.MAX_ARRAY_LENGTH}`,
      }
    }
    // 数组检查完成，直接返回
    return { shouldContinue: true }
  }

  // 检查对象大小（最慢，放在最后）
  // 优化策略：
  // 1. 只在对象不是数组时检查，避免重复检查
  // 2. 使用 Object.keys 但可以提前退出（虽然 Object.keys 会遍历完，但这是必要的安全检查）
  // 3. 对于大多数正常大小的对象，这个检查的开销是可以接受的
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    // 注意：Object.keys 会遍历所有可枚举的自有属性
    // 虽然这有性能开销，但对于性能保护来说是必要的
    // 如果对象真的很大，这个检查可以防止后续更昂贵的递归操作
    const keys = Object.keys(obj)
    if (keys.length > PERFORMANCE_CONFIG.MAX_OBJECT_SIZE) {
      return {
        shouldContinue: false,
        reason: `对象大小超过限制: ${keys.length} > ${PERFORMANCE_CONFIG.MAX_OBJECT_SIZE}`,
      }
    }
  }

  return { shouldContinue: true }
}

/**
 * 统一的错误处理函数
 */
export function handleError(
  error: Error,
  path: string,
  config: Required<ProxyHandlerConfig>,
  context?: string,
) {
  config.onConversionError(path, error, context)

  if (config.warnInConsole) {
    logMessage({
      level: 'error',
      prefix: 'ProxyHandler',
      message: `处理失败: ${path}`,
      data: error,
      context,
    })
  }
}
